import * as Yup from 'yup';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
// formik validation
import { Form, FormikProvider, useFormik } from 'formik';
// @mui
import { Stack, IconButton, InputAdornment, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// redux
import { useDispatch } from 'react-redux';
import { registrationAccount } from './AccountCreationAction';
// components
import Iconify from '../../../components/iconify';
import { PASSWORD_REGEX } from '../../../Constants/RegexConstants';

// ----------------------------------------------------------------------

export default function AccountCreationForm() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // state variables
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Schema for validation
  const RegisterSchema = Yup.object().shape({
    companyName: Yup.string().min(2, 'Too Short!').required('Company name is required'),
    userName: Yup.string().min(2, 'Too Short!').required('User name is required'),
    email: Yup.string().email('Email must be a valid email address').required('Email is required'),
    password: Yup.string()
      .required('Password is required')
      .matches(
        PASSWORD_REGEX,
        'Password should contain at least one number and one special character having length between 6 To 16 characters.'
      ),
    confirmPassword: Yup.string()
      .required('Confirm password is required')
      .oneOf([Yup.ref('password')], 'Passwords do not match'),
    userTimezone: Yup.string().default('Asia/Calcutta'),
  });

  const formik = useFormik({
    initialValues: {
      companyName: '',
      userName: '',
      email: '',
      password: '',
      confirmPassword: '',
      userTimezone: 'Asia/Calcutta',
    },
    validationSchema: RegisterSchema,

    onSubmit: (values) => {
      const body = { ...values };
      delete body.confirmPassword;
      dispatch(registrationAccount(body))
        .then(({ value }) => {
          if (value.data.status === 'success') {
            navigate('/auth/login', { replace: true });
            toast.success('Registration Successful');
            toast.success('Please check your email to confirm your account');
          }
        })
        .catch((error) => {
          toast.error(error?.response?.data?.message ?? 'Error while creation');
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
  });

  const { errors, touched, handleSubmit, getFieldProps, isSubmitting, setSubmitting } = formik;

  return (
    <>
      <FormikProvider value={formik}>
        <Form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <TextField
              name="companyName"
              label="Company Name"
              {...getFieldProps('companyName')}
              error={touched.companyName && Boolean(errors.companyName)}
              helperText={touched.companyName && errors.companyName}
            />
            <TextField
              name="userName"
              label="User Name"
              {...getFieldProps('userName')}
              error={touched.userName && Boolean(errors.userName)}
              helperText={touched.userName && errors.userName}
            />

            <TextField
              name="email"
              label="Email address"
              {...getFieldProps('email')}
              error={touched.email && Boolean(errors.email)}
              helperText={touched.email && errors.email}
            />

            <TextField
              name="password"
              label="Password"
              {...getFieldProps('password')}
              error={touched.password && Boolean(errors.password)}
              helperText={touched.password && errors.password}
              type={showPassword ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              name="confirmPassword"
              label="Confirm Password"
              {...getFieldProps('confirmPassword')}
              error={touched.confirmPassword && Boolean(errors.confirmPassword)}
              helperText={touched.confirmPassword && errors.confirmPassword}
              type={showConfirmPassword ? 'text' : 'password'}
              style={{ marginBottom: '24px' }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                      <Iconify icon={showConfirmPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>

          <LoadingButton fullWidth size="large" type="submit" variant="contained" loading={isSubmitting}>
            Create Account
          </LoadingButton>
        </Form>
      </FormikProvider>
    </>
  );
}
